/**
 * The loadboard's PINNED boards — the strip's leftmost pills, which are not search tabs: they run no lane
 * query, cannot be closed, and are never the default view.
 *
 * `driver_bids` lists every load the application's drivers bid on. `my_bids` lists the loads the signed-in
 * dispatcher has sent a bid email about — the Bidding page's dispatcher filter pointed at the caller, on the
 * board where they are already working.
 */
export type BoxTruckPinnedBoard = 'driver_bids' | 'my_bids'

/** A pinned board borrows no tab id — it shows while `activeTabId` still points at the last-focused SEARCH
 *  tab — so every per-board bucket (refinements, find term, hidden sources, open load, scroll position) keys
 *  its state under one of these RESERVED ids instead. Without them a pinned board's filters bind to that lane
 *  tab and bleed both ways: the lane's Min $ silently filters the bids board, and clearing it there wipes the
 *  lane's own refinements. */
const PINNED_BOARD_KEYS: Record<BoxTruckPinnedBoard, string> = {
    driver_bids: '__driver_bids__',
    my_bids: '__my_bids__',
}

export const DRIVER_BIDS_KEY = PINNED_BOARD_KEYS.driver_bids
export const MY_BIDS_KEY = PINNED_BOARD_KEYS.my_bids

/** Which per-board bucket the view belongs to: the pinned board's reserved key, else the active search tab. */
export const boardStateKey = (pinnedBoard: BoxTruckPinnedBoard | null, activeTabId: string): string =>
    pinnedBoard ? PINNED_BOARD_KEYS[pinnedBoard] : activeTabId

/** Built once: the scroll-position writer asks this on EVERY scroll event, and an `Object.values().includes`
 *  there allocates an array per event while the operator drags the board. */
const PINNED_BOARD_KEY_SET: ReadonlySet<string> = new Set(Object.values(PINNED_BOARD_KEYS))

/** Whether a per-board bucket key belongs to a pinned board rather than a search tab. Callers that purge
 *  state for boards that no longer exist ask this first: a pinned board has no tab to be found among. */
export const isPinnedBoardKey = (key: string): boolean => PINNED_BOARD_KEY_SET.has(key)
